import React, { useState } from 'react';
import { createRequest } from '../firebase';
import { useUser } from '../UserContext';
import { Link } from 'react-router-dom';

const BorrowToolForm = ({ tool, owner }) => {
  const [borrowDate, setBorrowDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [message, setMessage] = useState('');
  const [requestError, setRequestError] = useState(null);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const { user } = useUser();

  const handleRequestTool = async (e) => {
    e.preventDefault();
    if (!user) {
      setRequestError('Please log in to request a tool.');
      return;
    }

    console.log('Attempting to create borrow request with the following data:');
    console.log('Tool:', tool);
    console.log('Owner:', owner);
    console.log('Current User:', user);
    console.log('Borrow Date:', borrowDate);
    console.log('Return Date:', returnDate);
    console.log('Message:', message);

    try {
      setRequestError(null);
      const requestedDates = [{ startDate: borrowDate, endDate: returnDate }];
      const result = await createRequest(tool.id, user.uid, owner.id, requestedDates, message);
      console.log('Borrow request created successfully:', result);
      setRequestSuccess(true);
    } catch (error) {
      console.error('Error creating borrow request:', error);
      if (error.message === "Borrower not found" || error.message === "Owner not found") {
        setRequestError('There was an issue with your account. Please try logging out and back in.');
      } else if (error.message === "Tool not found") {
        setRequestError('The requested tool is no longer available. Please refresh the page and try again.');
      } else if (error.code === "permission-denied") {
        setRequestError('You do not have permission to perform this action. Please check your account status.');
        console.error('Permission denied error. User:', user);
      } else {
        setRequestError(`Failed to send borrow request: ${error.message}`);
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (!user) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">Borrow This Tool</h2>
        <p className="text-gray-700 mb-4">
          To borrow this tool, you need to be logged in. Please sign up or log in to continue.
        </p>
        <div className="flex space-x-4">
          <Link
            to="/login"
            className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 transition duration-200"
          >
            Log In
          </Link>
          <Link
            to="/signup"
            className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
          >
            Sign Up
          </Link>
        </div>
      </div>
    );
  }

  if (requestSuccess) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-4 text-green-600">Request Sent Successfully!</h2>
        <p className="text-gray-700">
          Your request to borrow {tool.name} from {formatDate(borrowDate)} to {formatDate(returnDate)} has been sent to the owner. They will review your request and get back to you soon.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <h2 className="text-2xl font-semibold mb-4">Borrow This Tool</h2>
      {requestError && (
        <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
          {requestError}
        </div>
      )}
      <form onSubmit={handleRequestTool}>
        <div className="mb-4">
          <label htmlFor="borrowDate" className="block text-sm font-medium text-gray-700 mb-2">
            Borrow Date
          </label>
          <input
            type="date"
            id="borrowDate"
            className="w-full p-2 border border-gray-300 rounded"
            value={borrowDate}
            onChange={(e) => setBorrowDate(e.target.value)}
            required
          />
        </div>
        <div className="mb-4">
          <label htmlFor="returnDate" className="block text-sm font-medium text-gray-700 mb-2">
            Return Date
          </label>
          <input
            type="date"
            id="returnDate"
            className="w-full p-2 border border-gray-300 rounded"
            value={returnDate}
            onChange={(e) => setReturnDate(e.target.value)}
            required
          />
        </div>
        <div className="mb-4">
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            Message to the owner
          </label>
          <textarea
            id="message"
            className="w-full p-2 border border-gray-300 rounded"
            rows="3"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter a message for the tool owner..."
            required
          ></textarea>
        </div>
        <button 
          type="submit"
          className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
        >
          Request Tool
        </button>
      </form>
    </div>
  );
};

export default BorrowToolForm;